// frontend/src/components/admin/reseller/useResellerState.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useSnackbar } from "notistack";
import { adminAPI, esimAPI } from "../../../services/api";
import debounce from "lodash/debounce";

export const useResellerState = () => {
  const [resellers, setResellers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [availablePackages, setAvailablePackages] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  // Use useRef for the debounced function to maintain reference
  const debouncedFetchRef = useRef(null);

  // Base fetch function
  const fetchResellersBase = useCallback(
    async (retryCount = 0) => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminAPI.getAllResellers();
        setResellers(response.data);
      } catch (err) {
        console.error("Fetch error:", err);

        if (err.response?.status === 429 && retryCount < 3) {
          const retryAfter = parseInt(err.response.headers["retry-after"]) || 5;
          await new Promise((resolve) =>
            setTimeout(resolve, retryAfter * 1000)
          );
          return fetchResellersBase(retryCount + 1);
        }

        setError(err.response?.data?.message || err.message);
        enqueueSnackbar(
          err.response?.data?.message || "Failed to fetch resellers",
          { variant: "error" }
        );
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  // Initialize debounced function
  useEffect(() => {
    debouncedFetchRef.current = debounce((retryCount = 0) => {
      fetchResellersBase(retryCount);
    }, 1000);

    return () => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, [fetchResellersBase]);

  // Wrapper function to call the debounced fetch
  const fetchResellers = useCallback(() => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current();
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await esimAPI.getPackages();
      if (response.data.obj?.packageList) {
        setAvailablePackages(response.data.obj.packageList);
      }
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      // Don't show error notification for packages as it's not critical
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchResellers();
    fetchPackages();
  }, [fetchResellers, fetchPackages]);

  return {
    resellers,
    setResellers,
    error,
    setError,
    loading,
    setLoading,
    submitLoading,
    setSubmitLoading,
    selectedReseller,
    setSelectedReseller,
    availablePackages,
    fetchResellers,
    enqueueSnackbar,
  };
};
